import React, { useState, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Autocomplete,
	TextField,
} from "@mui/material";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";

const AssignEquipmentDialog = ({
	open,
	onClose,
	onSave,
	vehicle,
	equipment = [],
	employees = [],
}) => {
	const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
	const [notes, setNotes] = useState("");

	// Filter to only show unassigned equipment (not assigned to any vehicle)
	const unassignedEquipment = useMemo(() => {
		return equipment.filter(
			(eq) =>
				!eq.vehicleId &&
				eq.status !== "Retired" &&
				!eq.isDeleted,
		);
	}, [equipment]);

	// Build options for autocomplete
	const equipmentOptions = useMemo(() => {
		return unassignedEquipment.map((eq) => ({
			value: eq.id,
			label: `${eq.type || ""} — ${[eq.brand, eq.model].filter(Boolean).join(" ")} · ${eq.serialNumber || eq.assetTag || eq.propertyNumber || `#${eq.id}`}`,
			equipment: eq,
		}));
	}, [unassignedEquipment]);

	const handleClose = () => {
		setSelectedEquipmentId(null);
		setNotes("");
		onClose();
	};

	const handleSave = () => {
		if (!selectedEquipmentId) return;

		const selectedEquipment = equipment.find(
			(eq) => eq.id === selectedEquipmentId,
		);

		onSave({
			equipmentId: selectedEquipmentId,
			vehicleId: vehicle.id,
			notes: notes.trim() || "Assigned via Vehicle Detail",
			equipment: selectedEquipment,
		});

		handleClose();
	};

	const selectedOption = equipmentOptions.find(
		(opt) => opt.value === selectedEquipmentId,
	);

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>
				+ Assign Equipment — {vehicle?.unitNumber}
			</DialogTitle>

			<DialogContent>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						pt: 1,
					}}
				>
					{/* Vehicle Summary */}
					<Box
						sx={{
							display: "flex",
							gap: 1.5,
							p: 1.5,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							alignItems: "center",
							bgcolor: "background.default",
						}}
					>
						<Typography sx={{ fontSize: 22 }}>🚗</Typography>
						<Box>
							<Typography variant="body2" fontWeight={700}>
								{vehicle?.unitNumber} — {vehicle?.year}{" "}
								{vehicle?.make} {vehicle?.model}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								{vehicle?.division || "—"} ·{" "}
								{vehicle?.status || "—"}
							</Typography>
						</Box>
					</Box>

					{/* Equipment Selection */}
					<Box>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								mb: 1,
								display: "block",
							}}
						>
							Select Equipment to Assign
						</Typography>
						<Autocomplete
                            label="Equipment"
							options={equipmentOptions}
							value={selectedOption || null}
							onChange={(_, newValue) => {
								setSelectedEquipmentId(newValue?.value || null);
							}}
							getOptionLabel={(option) => option.label || ""}
							isOptionEqualToValue={(option, value) =>
								option.value === value?.value
							}
							renderInput={(params) => (
								<TextField
									{...params}
									variant="outlined"
									size="small"
								/>
							)}
							renderOption={(props, option) => (
								<Box
									component="li"
									{...props}
									sx={{ fontSize: 13 }}
								>
									<Box>
										<Typography
											variant="body2"
											fontWeight={600}
										>
											{option.equipment?.type || "—"}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block" }}
										>
											{[
												option.equipment?.brand,
												option.equipment?.model,
											]
												.filter(Boolean)
												.join(" ") || "—"}{" "}
											·{" "}
											{option.equipment?.serialNumber ||
												option.equipment?.assetTag ||
												"—"}
										</Typography>
									</Box>
								</Box>
							)}
							fullWidth
						/>
					</Box>

					{/* No unassigned equipment message */}
					{unassignedEquipment.length === 0 && (
						<Box
							sx={{
								p: 2,
								bgcolor: "background.default",
								border: 1,
								borderColor: "divider",
								borderRadius: 1,
								textAlign: "center",
							}}
						>
							<Typography variant="body2" color="text.secondary">
								All equipment is currently assigned to a vehicle
								or employee.
							</Typography>
							<Typography variant="caption" color="text.disabled">
								Unassign equipment from other vehicles first to
								make them available.
							</Typography>
						</Box>
					)}

					{/* Selected Equipment Preview */}
					{selectedOption && (
						<Box
							sx={{
								p: 1.5,
								bgcolor: "rgba(99,102,241,0.06)",
								border: "1px solid rgba(99,102,241,0.2)",
								borderRadius: 1,
							}}
						>
							<Typography
								variant="caption"
								fontWeight={700}
								color="text.secondary"
								sx={{
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									mb: 1,
									display: "block",
								}}
							>
								Selected Equipment
							</Typography>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 1,
								}}
							>
								<Box>
									<Typography
										variant="caption"
										color="text.disabled"
									>
										Type
									</Typography>
									<Typography
										variant="body2"
										fontWeight={600}
									>
										{selectedOption.equipment?.type || "—"}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="caption"
										color="text.disabled"
									>
										Serial Number
									</Typography>
									<Typography
										variant="body2"
										fontFamily="monospace"
									>
										{selectedOption.equipment
											?.serialNumber || "—"}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="caption"
										color="text.disabled"
									>
										Brand / Model
									</Typography>
									<Typography variant="body2">
										{[
											selectedOption.equipment?.brand,
											selectedOption.equipment?.model,
										]
											.filter(Boolean)
											.join(" ") || "—"}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="caption"
										color="text.disabled"
									>
										Condition
									</Typography>
									<Typography variant="body2">
										{selectedOption.equipment?.condition ||
											"—"}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATSaveButton
					onClick={handleSave}
					disabled={!selectedEquipmentId}
					tooltipText={`Assign to ${vehicle?.unitNumber}`}
				/>
				<PATCloseButton onClick={handleClose} tooltipText="Cancel" />
			</DialogActions>
		</Dialog>
	);
};

export default AssignEquipmentDialog;
